/*
 * Copyright (C) 2014 Spanning Cloud Apps. LLC  All rights reserved.
 */
import './overwriteValues.js';
import './settings.tpl.html';
import './specificFields.js';

import * as _ from 'underscore';

angular
  .module('bulkRestore.settings', ['services.error'])
  /**
   * Controller for the restore.confirm template.
   */
  .controller('BulkRestoreSettingsCtrl', [
    '$scope',
    '$rootScope',
    '$state',
    '$filter',
    '$q',
    'BulkRestoreService',
    'ErrorService',
    'WizardService',
    'confirmModalUtils',
    function (
      $scope,
      $rootScope,
      $state,
      $filter,
      $q,
      BulkRestoreService,
      ErrorService,
      WizardService,
      confirmModalUtils
    ) {
      if (!$state.params.bulkRestoreData) {
        throw new Error('bulkRestoreData is required');
      }

      var i18n = $filter('i18n');
      var bulkRestoreData = $state.params.bulkRestoreData;
      var model;

      // Published functions
      $scope.canSpecifyFields = canSpecifyFields;
      $scope.checkForReference = checkForReference;
      $scope.getPageTitle = getPageTitle;
      $scope.getRecordNamesText = getRecordNamesText;
      $scope.isOverwritingValues = isOverwritingValues;
      $scope.isSpecifyingFields = isSpecifyingFields;
      $scope.isValid = isValid;
      $scope.loadReferencesPage = loadReferencesPage;
      $scope.toggleOverwritingValues = toggleOverwritingValues;
      $scope.toggleShowReferences = toggleShowReferences;
      $scope.toggleSpecifyingFields = toggleSpecifyingFields;

      $scope.canIncludeChildren = canIncludeChildren;
      $scope.isIncludingChildren = isIncludingChildren;
      $scope.toggleIncludingChildren = toggleIncludingChildren;
      $scope.isRepairingLookupRelationships = isRepairingLookupRelationships;
      $scope.toggleRepairingLookup = toggleRepairingLookup;
      $scope.isRestoringLookupRelationships = isRestoringLookupRelationships;
      $scope.toggleRestoringLookup = toggleRestoringLookup;
      $scope.isDisablingSalesforceAutomation = isDisablingSalesforceAutomation;
      $scope.toggleSalesforceAutomation = toggleSalesforceAutomation;

      // Publish the bulk restore data
      $scope.bulkRestoreData = bulkRestoreData;

      $scope.lastLoadedReferenceObjectType = null;

      $scope.model = model = {
        hasReferences: false, // Until we check below
        kbLinks: {
          includeChildren: $rootScope.config.documentationLinks.includeChildrenInfo,
          overwriteValues: $rootScope.config.documentationLinks.overwriteValuesInfo,
          specificFields: $rootScope.config.documentationLinks.specificFieldsInfo,
          references: $rootScope.config.documentationLinks.restoreReferencesInfo
        },
        recordCount: bulkRestoreData.getSelectedObjectRecordsCountInfo().text,
        references: {},
        showReferences: false
      };

      // Register our page with the wizard service
      var page = WizardService.getCurrentPage();
      page.setHeaderText(i18n('SELECT_RESTORE_SETTINGS'));
      page.setStateParams($state.params);
      page.setValid(isValid);
      // For the purposes of confirming cancellation of the wizard we'll consider the wizard touched if we get this far.
      page.setTouched(true);

      // We have to see what references are present in order to know whether to display the "show references" button.
      $scope.loadReferencesPage();

      /**
       * Utility method to get the value to display for the setting title section.
       *
       * @returns {string} The title text to display.
       */
      function getPageTitle() {
        var title, objectType;

        const selectedObjectTypes = bulkRestoreData.getSelectedObjectTypes();
        if (!selectedObjectTypes) {
          if (bulkRestoreData.isRestoreFromCsv()) {
            objectType = bulkRestoreData.getSelectedObjectRecordTypes().join(',');
          } else {
            objectType = bulkRestoreData.getSearchObjectType();
          }

          if (model.recordCount === 1) {
            title = i18n('CONFIRM_RESTORE_RECORD', objectType);
          } else {
            title = i18n('CONFIRM_RESTORE_RECORDS', [model.recordCount, objectType]);
          }
        } else {
          if (selectedObjectTypes.selectedAll) {
            title = i18n('ALL_OBJECTS');
          } else {
            const selectedMultipleObjects = bulkRestoreData.checkObjectTypeSelectedCount(2);
            title = selectedMultipleObjects
              ? i18n('CONFIRM_RESTORE_OBJECTS', [
                  selectedObjectTypes.selectedList.length,
                  selectedObjectTypes.selectedList.join(', ')
                ])
              : i18n('CONFIRM_RESTORE_OBJECT', [selectedObjectTypes.selectedList[0]]);
          }
        }
        return title;
      }

      /**
       * Returns the text used to display the names of the selected records.
       *
       * @returns {string} the record names text.
       */
      function getRecordNamesText() {
        if (!bulkRestoreData.canGetSelectedObjectRecords()) {
          // This can happen if the user is selecting "all" and there is more than one page of results. In this case
          // we won't try to show the names.
          return '';
        }

        var names = bulkRestoreData.getSelectedObjectRecordNames();
        // We want to offset the names with a dash.
        return ' - ' + names.join(', ');
      }

      /**
       * Toggles whether the user wants to overwrite any field values.
       */
      function toggleOverwritingValues() {
        bulkRestoreData.setOverwritingValues(!bulkRestoreData.isOverwritingValues());
      }

      /**
       * Toggles whether the user wants to specify which fields to restore.
       */
      function toggleSpecifyingFields() {
        bulkRestoreData.setSpecifyingFields(!bulkRestoreData.isSpecifyingFields());
      }

      /**
       * Determines whether the user wants to overwrite any field values.
       *
       * @returns {boolean} true if the user wants to overwrite any field values.
       */
      function isOverwritingValues() {
        return bulkRestoreData.isOverwritingValues();
      }

      /**
       * Determines whether the user wants to specify which fields to restore.
       *
       * @returns {boolean} true if the user wants to specify which fields to restore.
       */
      function isSpecifyingFields() {
        return bulkRestoreData.isSpecifyingFields();
      }

      /**
       * A Utility method to see if the specific fields picker should be shown.
       *
       * @returns {boolean} - If the specific fields should be shown.
       */
      function canSpecifyFields() {
        return true;
      }

      /**
       * Toggles whether the reference fields are shown.
       */
      function toggleShowReferences() {
        model.showReferences = !model.showReferences;
      }

      /**
       * Determines whether the settings on the page are valid (i.e. Next is allowed in the wizard)
       *
       * @returns {boolean} true if the settings are valid, false otherwise.
       */
      function isValid() {
        return bulkRestoreData.specificFieldsAreValid() && bulkRestoreData.overwriteValuesAreValid();
      }

      /**
       * Checks the fields of the current object types and populates the list of fields that are references to other
       * object.
       *
       * @returns {Promise} a promise that resolves when all object types have been checked.
       */
      function loadReferencesPage() {
        // Use the secondary loading mask because the regular loading mask is turned on and off for each object type
        // request.
        $rootScope.showSecondaryLoadingMask = true;
        const pageSize = 50;

        let types = bulkRestoreData.getSelectedObjectTypesList();
        types.sort();
        if ($scope.lastLoadedReferenceObjectType) {
          types = types.filter((type) => type > $scope.lastLoadedReferenceObjectType);
        }
        const typesPage = types.slice(0, pageSize);
        const typesPageChunks = _.chunk(typesPage, 5);

        // For each object type get the fields
        const promises = [];
        _.each(typesPageChunks, function (typesPageChunk) {
          const promise = BulkRestoreService.getFieldsForObjectTypes(typesPageChunk).then(function (result) {
            // For each field check for references
            for (const objectType of Object.keys(result)) {
              const fields = result[objectType];
              for (const fieldKey of Object.keys(fields)) {
                checkForReference(objectType, fields[fieldKey]);
              }
            }
          });

          promises.push(promise);
        });

        return $q
          .all(promises)
          .then(function () {
            // model.references is built up in checkForReference()
            var count = _.size(model.references);
            model.hasReferences = model.showReferences = count > 0;

            // If there's only one type, pre-expand it
            if (count === 1) {
              var key = _.keys(model.references)[0];
              model.references[key].isExpanded = true;
            }
            if (typesPage.length === pageSize) {
              $scope.lastLoadedReferenceObjectType = typesPage[typesPage.length - 1];
            } else {
              $scope.lastLoadedReferenceObjectType = null;
            }
          })
          .catch(function (err) {
            $scope.lastLoadedReferenceObjectType = null;
            ErrorService.report(err);
          })
          .finally(function () {
            $rootScope.showSecondaryLoadingMask = false;
          });
      }

      /**
       * Determines whether the specified field is a reference field. If so, it is added to the map of reference fields.
       *
       * @param {string} objectType - the current object type
       * @param {object} field - the field description
       */
      function checkForReference(objectType, field) {
        if (field.type === 'reference') {
          var objectRefs = model.references[objectType];
          if (!objectRefs) {
            objectRefs = model.references[objectType] = {
              fields: [], // The list of reference fields for this type
              isExpanded: false // Whether this type is expanded in the view
            };
          }
          objectRefs.fields.push(field);
        }
      }

      /**
       * Toggles whether the user wants to include children of deleted records.
       */
      function toggleIncludingChildren() {
        bulkRestoreData.setIncludingChildren(!bulkRestoreData.isIncludingAllChildren());
      }

      /**
       * Determines whether the user wants to include children of deleted records.
       *
       * @returns {boolean} true if the user wants to include children.
       */
      function isIncludingChildren() {
        return bulkRestoreData.isIncludingAllChildren();
      }

      /**
       * Toggles whether the user wants to repair lookup relationships.
       */
      function toggleRepairingLookup() {
        bulkRestoreData.setRepairingLookupRelationships(!bulkRestoreData.isRepairingLookupRelationships());
      }

      /**
       * Determines whether the user wants to repair lookup relationships in this restore.
       *
       * @returns {boolean} true if the user wants to repair lookup relationships.
       */
      function isRepairingLookupRelationships() {
        return bulkRestoreData.isRepairingLookupRelationships();
      }

      /**
       * Toggles whether the user wants to restore lookup relationships.
       */
      function toggleRestoringLookup() {
        bulkRestoreData.setRestoringLookupRelationships(!bulkRestoreData.isRestoringLookupRelationships());
      }

      /**
       * Determines whether the user wants to restore lookup relationships in this restore.
       *
       * @returns {boolean} true if the user wants to restore lookup relationships.
       */
      function isRestoringLookupRelationships() {
        return bulkRestoreData.isRestoringLookupRelationships();
      }

      /**
       * Determines whether the user wants to disable salesforce automation in this restore.
       *
       * @returns {boolean} true if the user wants to disable salesforce automation.
       */
      function isDisablingSalesforceAutomation() {
        return bulkRestoreData.isDisablingSalesforceAutomation();
      }

      /**
       * Toggles whether the user wants to disable salesforce automation.
       */
      function toggleSalesforceAutomation() {
        const previousState = bulkRestoreData.isDisablingSalesforceAutomation();
        if (!previousState) {
          confirmModalUtils
            .openConfirmModal({
              titleTextKey: 'CONFIRM_DISABLE_AUTOMATION_TITLE',
              confirmTextKey: 'CONFIRM_DISABLE_AUTOMATION_DESC'
            })
            .then((isConfirmed) => {
              if (isConfirmed) {
                bulkRestoreData.setDisableSalesforceAutomation(!previousState);
              }
            });
        } else {
          bulkRestoreData.setDisableSalesforceAutomation(!previousState);
        }
      }

      /**
       * Determines whether the option to include children in the restore should be shown
       *
       * @returns {boolean} true if the option should be shown.
       */
      function canIncludeChildren() {
        return true;
      }
    }
  ]);
